import { useState } from "react";
import { collection, addDoc, doc, setDoc, deleteDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, FIREBASE_READY, STORAGE_READY } from "../../firebase";
import { useApp } from "../../contexts/AppContext";
import { byDateDesc } from "../../data/initialData";
import { sSave } from "../../utils/storage";
import { handleImageUpload } from "../../utils/imageUpload";

const PAGE_SIZE = 8;
const blankPost = () => ({ title:"", date:new Date().toISOString().slice(0,10), category:"Announcement", excerpt:"", content:"", image:null, published:true });

export default function PostsTab({ flash, flashError, uploading, setUploading }) {
  const { posts, setPosts } = useApp();
  const [editPost, setEditPost] = useState(null);
  const [newPost,  setNewPost]  = useState(blankPost());
  const [adminPage, setAdminPage] = useState(1);

  const EP    = editPost || newPost;
  const setEP = v => {
    const next = typeof v === "function" ? v(EP) : v;
    editPost ? setEditPost(next) : setNewPost(next);
  };

  const handlePostImg = async (file) => {
    if (!file) return;
    if (FIREBASE_READY && STORAGE_READY) {
      if (file.size > 3 * 1024 * 1024) { flashError("Image is too large — please use a file under 3 MB."); return; }
      setUploading(true);
      try {
        const path = `posts/${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
        const sr   = storageRef(storage, path);
        await uploadBytes(sr, file, { contentType: file.type });
        const url  = await getDownloadURL(sr);
        setEP(ep => ({ ...ep, image: url }));
        flash("Post image uploaded.");
      } catch (e) {
        console.error("Post image upload error:", e);
        const code = e?.code ?? "";
        if (code === "storage/unauthorized" || code === "storage/unauthenticated")
          flashError("Permission denied — make sure you are signed in as admin.");
        else
          flashError(`Upload failed (${code || e?.message || "unknown error"}). Check browser console.`);
      } finally {
        setUploading(false);
      }
    } else if (FIREBASE_READY && !STORAGE_READY) {
      flashError("Firebase Storage is not yet activated. Please set it up in the Firebase Console first.");
    } else {
      handleImageUpload(file, img => setEP(ep => ({ ...ep, image: img })));
    }
  };

  const savePost = async () => {
    const { id: docId, ...postData } = EP;
    if (FIREBASE_READY) {
      try {
        if (editPost) await setDoc(doc(db, "posts", docId), postData);
        else          await addDoc(collection(db, "posts"), postData);
      } catch { flash("Error saving post."); return; }
    } else {
      const id = editPost?.id || Date.now();
      const p  = editPost ? { id: docId, ...postData } : { ...postData, id };
      const up = editPost ? posts.map(x => x.id === p.id ? p : x) : [...posts, p];
      setPosts(up);
      sSave("lhda_posts", up);
    }
    setEditPost(null);
    setNewPost(blankPost());
    setAdminPage(1);
    flash("Post saved!");
  };

  const delPost = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    if (FIREBASE_READY) {
      try { await deleteDoc(doc(db, "posts", id)); } catch { flash("Error deleting."); return; }
    } else {
      const u = posts.filter(p => p.id !== id);
      setPosts(u);
      sSave("lhda_posts", u);
    }
    flash("Post deleted.");
  };

  const sorted     = [...posts].sort(byDateDesc);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const curPage    = Math.min(adminPage, totalPages);
  const paginated  = sorted.slice((curPage - 1) * PAGE_SIZE, curPage * PAGE_SIZE);

  return (
    <div>
      {/* ── Form ── */}
      <div className="acard">
        <div className="atitle">{editPost ? "✏️ Edit Post" : "✍️ New Post"}</div>
        <div className="grid2">
          <div className="field"><label>Title</label>
            <input value={EP.title} onChange={e => setEP({ ...EP, title:e.target.value })} placeholder="Post title"/>
          </div>
          <div className="field"><label>Date</label>
            <input type="date" value={EP.date} onChange={e => setEP({ ...EP, date:e.target.value })}/>
          </div>
        </div>
        <div className="field"><label>Category</label>
          <select value={EP.category} onChange={e => setEP({ ...EP, category:e.target.value })}>
            {["Announcement","Event","Project Update","News","Notice"].map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="field"><label>Excerpt (shown in news cards)</label>
          <textarea value={EP.excerpt} onChange={e => setEP({ ...EP, excerpt:e.target.value })} placeholder="Brief summary…" rows={3}/>
        </div>
        <div className="field"><label>Full Content</label>
          <textarea value={EP.content} onChange={e => setEP({ ...EP, content:e.target.value })} placeholder="Full article text…" rows={8}/>
        </div>
        <div className="field">
          <label>Post Image (optional — max 3MB)</label>
          {!EP.image ? (
            <div className="img-upload-box">
              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={e => handlePostImg(e.target.files[0])}/>
              <div className="img-upload-icon">🖼️</div>
              <div className="img-upload-txt">Click to upload an image</div>
              <div className="img-upload-sub">JPG, PNG, WEBP or GIF — max 3MB</div>
            </div>
          ) : (
            <div className="img-preview-wrap">
              <img className="img-preview" src={EP.image} alt="preview"/>
              <button className="img-remove-btn" onClick={() => setEP({ ...EP, image:null })}>✕ Remove</button>
            </div>
          )}
        </div>
        <label style={{ display:"flex", alignItems:"center", gap:".5rem", cursor:"pointer", fontWeight:600, fontSize:".85rem", marginBottom:"1rem" }}>
          <input type="checkbox" checked={EP.published} onChange={e => setEP({ ...EP, published:e.target.checked })}/> Publish immediately
        </label>
        <div className="row-btns">
          <button className="btn-g" onClick={savePost} disabled={uploading}>{editPost ? "Update Post" : "Publish Post"}</button>
          {editPost && <button className="btn-gray" onClick={() => setEditPost(null)}>Cancel</button>}
        </div>
      </div>

      {/* ── List ── */}
      <div className="acard">
        <div className="atitle">All Posts <span style={{ fontSize:".75rem", fontWeight:400, color:"var(--gr)" }}>({posts.length}) — newest first</span></div>
        {sorted.length === 0
          ? <div className="empty-state">No posts yet.</div>
          : (
            <>
              {paginated.map(post => (
                <div key={post.id} className="pi-row">
                  <div style={{ display:"flex", alignItems:"center", gap:".75rem" }}>
                    {post.image
                      ? <img src={post.image} alt="" style={{ width:44, height:44, borderRadius:8, objectFit:"cover", flexShrink:0 }}/>
                      : <div style={{ width:44, height:44, borderRadius:8, background:"var(--gp)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem", flexShrink:0 }}>📰</div>
                    }
                    <div>
                      <div className="pi-title">{post.title}</div>
                      <div className="pi-meta">{post.date} · {post.category} · {post.published ? "✅ Published" : "⚪ Draft"}</div>
                    </div>
                  </div>
                  <div className="pi-acts">
                    <button className="btn-b" onClick={() => { setEditPost({ ...post }); window.scrollTo({ top:0, behavior:"smooth" }); }}>Edit</button>
                    <button className="btn-r" onClick={() => delPost(post.id)}>Delete</button>
                  </div>
                </div>
              ))}
              {totalPages > 1 && (
                <div className="pagination" style={{ marginTop:"1rem" }}>
                  <button disabled={curPage === 1} onClick={() => setAdminPage(p => p - 1)}>← Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button key={n} className={curPage === n ? "active" : ""} onClick={() => setAdminPage(n)}>{n}</button>
                  ))}
                  <button disabled={curPage === totalPages} onClick={() => setAdminPage(p => p + 1)}>Next →</button>
                </div>
              )}
            </>
          )
        }
      </div>
    </div>
  );
}
